import { useEffect, useState, useCallback } from 'react';

/**
 * Full Telegram Mini Apps SDK Wrapper Hook
 */
export function useTelegram() {
    const tg = typeof window !== 'undefined' ? (window as any).Telegram?.WebApp : null;

    const ready = useCallback(() => tg?.ready?.(), [tg]);
    const expand = useCallback(() => tg?.expand?.(), [tg]);
    const requestFullscreen = useCallback(() => {
        try { tg?.requestFullscreen?.(); } catch (e) { }
    }, [tg]);
    const close = useCallback(() => tg?.close?.(), [tg]);

    const setHeaderColor = useCallback((color: string) => {
        try { tg?.setHeaderColor?.(color); } catch (e) { }
    }, [tg]);

    const setBackgroundColor = useCallback((color: string) => {
        try { tg?.setBackgroundColor?.(color); } catch (e) { }
    }, [tg]);

    const enableClosingConfirmation = useCallback(() => {
        try { tg?.enableClosingConfirmation?.(); } catch (e) { }
    }, [tg]);

    const disableClosingConfirmation = useCallback(() => {
        try { tg?.disableClosingConfirmation?.(); } catch (e) { }
    }, [tg]);

    const openLink = useCallback((url: string, options?: any) => {
        if (tg?.openLink) tg.openLink(url, options);
        else window.open(url, '_blank');
    }, [tg]);

    const openTelegramLink = useCallback((url: string) => {
        if (tg?.openTelegramLink) tg.openTelegramLink(url);
        else window.open(url, '_blank');
    }, [tg]);

    const openInvoice = useCallback((url: string, callback?: (status: string) => void) => {
        if (tg?.openInvoice) tg.openInvoice(url, callback);
    }, [tg]);

    const shareURL = useCallback((url: string, text?: string) => {
        if (tg?.shareURL) tg.shareURL(url, text);
        else if (navigator.share) navigator.share({ url, text }).catch(() => { });
    }, [tg]);

    const sendData = useCallback((data: string) => {
        if (tg?.sendData) tg.sendData(data);
    }, [tg]);

    const switchInlineQuery = useCallback((query: string, choose_chat_types?: string[]) => {
        if (tg?.switchInlineQuery) tg.switchInlineQuery(query, choose_chat_types);
    }, [tg]);

    const requestContact = useCallback((callback?: (sent: boolean) => void) => {
        if (tg?.requestContact) tg.requestContact(callback);
    }, [tg]);

    const requestWriteAccess = useCallback((callback?: (allowed: boolean) => void) => {
        if (tg?.requestWriteAccess) tg.requestWriteAccess(callback);
    }, [tg]);

    const readTextFromClipboard = useCallback((callback?: (text: string) => void) => {
        if (tg?.readTextFromClipboard) tg.readTextFromClipboard(callback);
    }, [tg]);

    return {
        tg,
        user: tg?.initDataUnsafe?.user || null,
        initData: tg?.initData || '',
        initDataUnsafe: tg?.initDataUnsafe || {},
        platform: tg?.platform || 'unknown',
        version: tg?.version || '1.0',
        colorScheme: tg?.colorScheme || 'dark',
        themeParams: tg?.themeParams || {},
        isExpanded: tg?.isExpanded || false,
        viewportHeight: tg?.viewportHeight || typeof window !== 'undefined' ? window.innerHeight : 0,
        viewportStableHeight: tg?.viewportStableHeight || typeof window !== 'undefined' ? window.innerHeight : 0,
        ready,
        expand,
        requestFullscreen,
        close,
        setHeaderColor,
        setBackgroundColor,
        enableClosingConfirmation,
        disableClosingConfirmation,
        openLink,
        openTelegramLink,
        openInvoice,
        shareURL,
        sendData,
        switchInlineQuery,
        requestContact,
        requestWriteAccess,
        readTextFromClipboard
    };
}

/**
 * Native Telegram Back Button Hook
 */
export function useTelegramBackButton(visible: boolean, onClick?: () => void) {
    useEffect(() => {
        const tg = typeof window !== 'undefined' ? (window as any).Telegram?.WebApp : null;
        if (!tg || !tg.BackButton) return;

        if (visible) {
            tg.BackButton.show();
        } else {
            tg.BackButton.hide();
        }

        if (onClick) {
            tg.BackButton.onClick(onClick);
            return () => {
                tg.BackButton.offClick(onClick);
            };
        }
    }, [visible, onClick]);
}

/**
 * Native Telegram Main Button Hook
 */
export function useTelegramMainButton(config: { text?: string; color?: string; textColor?: string; visible?: boolean; active?: boolean; loading?: boolean; onClick?: () => void }) {
    useEffect(() => {
        const tg = typeof window !== 'undefined' ? (window as any).Telegram?.WebApp : null;
        if (!tg || !tg.MainButton) return;

        const mb = tg.MainButton;
        if (config.text) mb.setText(config.text);
        if (config.color) mb.setParams({ color: config.color });
        if (config.textColor) mb.setParams({ text_color: config.textColor });
        
        if (config.loading) mb.showProgress(false);
        else mb.hideProgress();

        if (config.active === false) mb.disable();
        else mb.enable();

        if (config.visible) mb.show();
        else mb.hide();

        if (config.onClick) {
            mb.onClick(config.onClick);
            return () => {
                mb.offClick(config.onClick);
            };
        }
    }, [config.text, config.color, config.textColor, config.visible, config.active, config.loading, config.onClick]);
}

/**
 * Telegram Haptics Hook
 */
export function useTelegramHaptics() {
    const tg = typeof window !== 'undefined' ? (window as any).Telegram?.WebApp : null;

    const impactOccurred = useCallback((style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'medium') => {
        try { tg?.HapticFeedback?.impactOccurred(style); } catch (e) { }
    }, [tg]);

    const notificationOccurred = useCallback((type: 'error' | 'success' | 'warning') => {
        try { tg?.HapticFeedback?.notificationOccurred(type); } catch (e) { }
    }, [tg]);

    const selectionChanged = useCallback(() => {
        try { tg?.HapticFeedback?.selectionChanged(); } catch (e) { }
    }, [tg]);

    return { impactOccurred, notificationOccurred, selectionChanged };
}

/**
 * Telegram Theme Hook
 */
export function useTelegramTheme() {
    const tg = typeof window !== 'undefined' ? (window as any).Telegram?.WebApp : null;
    const [colorScheme, setColorScheme] = useState(tg?.colorScheme || 'dark');
    const [themeParams, setThemeParams] = useState(tg?.themeParams || {});

    useEffect(() => {
        if (!tg) return;
        const handleThemeChange = () => {
            setColorScheme(tg.colorScheme || 'dark');
            setThemeParams(tg.themeParams || {});
        };
        tg.onEvent?.('themeChanged', handleThemeChange);
        return () => {
            tg.offEvent?.('themeChanged', handleThemeChange);
        };
    }, [tg]);

    return { colorScheme, themeParams };
}

/**
 * Telegram Viewport Manager Hook
 */
export function useTelegramViewport() {
    const tg = typeof window !== 'undefined' ? (window as any).Telegram?.WebApp : null;
    const [height, setHeight] = useState(tg?.viewportHeight || typeof window !== 'undefined' ? window.innerHeight : 0);
    const [isExpanded, setIsExpanded] = useState(tg?.isExpanded || false);

    useEffect(() => {
        if (!tg) return;
        const handleViewportChange = (event: any) => {
            setHeight(tg.viewportHeight);
            setIsExpanded(tg.isExpanded);
        };
        tg.onEvent?.('viewportChanged', handleViewportChange);
        return () => {
            tg.offEvent?.('viewportChanged', handleViewportChange);
        };
    }, [tg]);

    return { height, isExpanded, expand: () => tg?.expand?.() };
}

/**
 * Telegram Cloud Storage Hook
 */
export function useTelegramCloudStorage() {
    const tg = typeof window !== 'undefined' ? (window as any).Telegram?.WebApp : null;
    const cs = tg?.CloudStorage;

    const setItem = useCallback((key: string, value: string): Promise<boolean> => {
        return new Promise((resolve) => {
            if (!cs) { localStorage.setItem(key, value); resolve(true); return; }
            cs.setItem(key, value, (err: any, stored: boolean) => resolve(!err && stored));
        });
    }, [cs]);

    const getItem = useCallback((key: string): Promise<string | null> => {
        return new Promise((resolve) => {
            if (!cs) { resolve(localStorage.getItem(key)); return; }
            cs.getItem(key, (err: any, value: string) => resolve(err ? null : value));
        });
    }, [cs]);

    const removeItem = useCallback((key: string): Promise<boolean> => {
        return new Promise((resolve) => {
            if (!cs) { localStorage.removeItem(key); resolve(true); return; }
            cs.removeItem(key, (err: any, removed: boolean) => resolve(!err && removed));
        });
    }, [cs]);

    return { setItem, getItem, removeItem };
}
